import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Archive,
  Eye,
  Loader2,
  Package,
  X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

function ReceptionHistoryModal({ isOpen, onClose, userId }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (!isOpen || !userId) return;

    let cancelled = false;
    async function fetchHistory() {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(`${API_URL}/api/inventory/reception-requests`, {
          params: { user_id: userId, status: 'active' },
        });
        if (!cancelled) setItems(data.items || []);
      } catch (err) {
        console.error('Error fetching reception history:', err);
        if (!cancelled) setError('No se pudo cargar el historial de envíos.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchHistory();
    return () => { cancelled = true; };
  }, [isOpen, userId]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
        <div
          className="w-full max-w-5xl bg-white rounded-2xl shadow-xl overflow-hidden"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <div>
              <h3 className="text-lg font-extrabold text-slate-900 font-montserrat flex items-center gap-2">
                <Package className="w-5 h-5 text-[#67A43C]" />
                Historial de envíos
              </h3>
              <p className="text-xs text-slate-500 font-montserrat">
                Solicitudes pendientes y recepciones aceptadas por Fast Max
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              title="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 max-h-[75vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16 gap-2 text-slate-400 font-montserrat text-sm">
                <Loader2 className="w-5 h-5 animate-spin" />
                Cargando historial...
              </div>
            ) : error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 font-montserrat">
                {error}
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
                <Archive className="w-10 h-10 text-slate-200" />
                <p className="text-sm font-montserrat">Aun no hay envios registrados.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[880px]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <Th>Producto</Th>
                      <Th>SKU</Th>
                      <Th>Fecha envio</Th>
                      <Th>Cant. enviada</Th>
                      <Th>Cant. recibida</Th>
                      <Th>Fecha recepcion</Th>
                      <Th>Estado</Th>
                      <Th>Imagen</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50/70">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {item.primary_image ? (
                              <img src={item.primary_image} alt={item.product_title} className="w-9 h-9 rounded-lg object-cover bg-slate-100" />
                            ) : (
                              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                                <Archive className="w-4 h-4 text-slate-300" />
                              </div>
                            )}
                            <span className="text-sm font-semibold text-slate-800 font-montserrat">
                              {item.product_title || '-'}
                            </span>
                          </div>
                        </td>
                        <Td muted>{item.sku || '-'}</Td>
                        <Td>{formatDate(item.requested_at)}</Td>
                        <Td strong>{item.requested_quantity ?? 0} uds</Td>
                        <Td strong>{item.accepted_quantity ?? 0} uds</Td>
                        <Td>{formatDate(item.reviewed_at)}</Td>
                        <td className="px-4 py-3">
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="px-4 py-3">
                          {item.reception_image_url ? (
                            <button
                              type="button"
                              onClick={() => setImagePreview(item)}
                              className="p-2 rounded-lg border border-slate-200 text-[#B1336E] hover:bg-[#B1336E]/10 transition-colors"
                              title="Ver imagen de recepcion"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          ) : (
                            <span className="text-xs text-slate-300 font-montserrat">Pendiente</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {imagePreview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4" onClick={() => setImagePreview(null)}>
          <div
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <p className="text-sm font-bold text-slate-900 font-montserrat">{imagePreview.product_title}</p>
                <p className="text-xs text-slate-500 font-montserrat">
                  Recepcion: {formatDate(imagePreview.reviewed_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setImagePreview(null)}
                className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 bg-slate-50">
              <img
                src={imagePreview.reception_image_url}
                alt="Imagen de recepcion"
                className="max-h-[75vh] w-full object-contain rounded-xl bg-white"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StatusBadge({ status }) {
  const label = {
    pending: 'Pendiente',
    accepted: 'Aceptada',
    partially_accepted: 'Parcial',
    rejected: 'Rechazada',
  }[status] || status || '-';

  const klass = {
    pending: 'bg-blue-50 text-blue-700',
    accepted: 'bg-green-50 text-green-700',
    partially_accepted: 'bg-amber-50 text-amber-700',
    rejected: 'bg-red-50 text-red-700',
  }[status] || 'bg-slate-50 text-slate-500';

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold font-montserrat ${klass}`}>
      {label}
    </span>
  );
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap font-montserrat">
      {children}
    </th>
  );
}

function Td({ children, strong = false, muted = false }) {
  return (
    <td className="px-4 py-3">
      <span className={`text-sm font-montserrat ${strong ? 'font-bold text-slate-800' : muted ? 'text-slate-500 font-mono' : 'text-slate-600'}`}>
        {children}
      </span>
    </td>
  );
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default ReceptionHistoryModal;
